import { useEffect, useMemo, useState } from 'react'

import { useMutation } from '@apollo/client'
import { where } from 'firebase/firestore'
import { isEqual } from 'lodash-es'

import fireChat from 'fireChat'
import useCurrent from 'Hooks/useCurrent'
import useEvent from 'Hooks/useEvent'
import useMounted from 'Hooks/useMounted'
import { Chat } from 'Types'

import { useChatAuth } from '../AuthProvider'
import { CREATE_CHAT } from '../const/gql/createChat'

interface Result {
  chat?: Chat
}

const useChat = (locationId: string | number): [Result] => {
  const { uid } = useChatAuth()
  const { accountId } = useCurrent()
  const [requestCreate, setRequestCreate] = useState(false)
  const [chatMap, setChatMap] = useState<Record<string, Chat>>({})
  const chat = useMemo(() => chatMap[locationId], [chatMap, locationId])
  const isMounted = useMounted()

  const [createChat] = useMutation(CREATE_CHAT)

  const queryConstraints = [where('accountId', '==', accountId), where('customerId', '==', uid)]
  const getChats = useEvent(async () => {
    const newChats = await fireChat.getChats(...queryConstraints)
    if (!isMounted()) return

    setChatMap((chatMap) => {
      const newChatMap = { ...chatMap }
      newChats.forEach((chat) => (newChatMap[chat.locationId] = chat))
      return newChatMap
    })
    setRequestCreate(true)
  })

  const subscribeOnChat = useEvent(() => {
    return fireChat.subscribeOnChats(...queryConstraints, (newChats, removedIds) => {
      if (!isMounted()) return

      setChatMap((chatMap) => {
        let newChatMap = chatMap
        newChats.forEach((chat) => {
          if (!isEqual(newChatMap[chat.locationId], chat)) {
            newChatMap = { ...chatMap }
            newChatMap[chat.locationId] = chat
          }
        })
        if (removedIds.length) {
          newChatMap = { ...chatMap }
          removedIds.forEach((id) => delete newChatMap[id])
        }
        return newChatMap
      })
    })
  })

  useEffect(() => {
    if (!uid) return

    setRequestCreate(false)
    setChatMap({})

    let unsubscribe: undefined | (() => void)
    void getChats().then(() => {
      if (!isMounted()) return
      unsubscribe = subscribeOnChat()
    })

    return () => unsubscribe?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid])

  useEffect(() => {
    if (!requestCreate || chatMap[locationId]) return

    void createChat({ variables: { locationId } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, requestCreate])

  return [{ chat }]
}

export default useChat
